import React, { useState } from 'react';
import { ChevronUp, ChevronDown, Download } from 'lucide-react';

const DataTable = ({ data, columns }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Convert to numbers if they look like numbers (including strings that represent numbers)
      const aNum = typeof aValue === 'number' ? aValue : parseFloat(aValue);
      const bNum = typeof bValue === 'number' ? bValue : parseFloat(bValue);

      // If both values are valid numbers, sort numerically
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Otherwise, sort as strings
      const aString = String(aValue).toLowerCase();
      const bString = String(bValue).toLowerCase();

      if (aString < bString) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aString > bString) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  const paginatedData = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) {
      return <ChevronUp className="h-4 w-4 text-slate-500" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className="h-4 w-4 text-purple-400" />
    ) : (
      <ChevronDown className="h-4 w-4 text-purple-400" />
    );
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  };

  const getColumnWidth = (column) => {
    const columnLower = column.toLowerCase();
    
    // Fixed widths for specific column types
    if (columnLower.includes('id') || columnLower.includes('_id')) {
      return 'w-24'; // 96px
    }
    if (columnLower.includes('name') || columnLower.includes('campaign')) {
      return 'w-48'; // 192px
    }
    if (columnLower.includes('url')) {
      return 'w-64'; // 256px
    }
    if (columnLower.includes('spend') || columnLower.includes('revenue') || columnLower.includes('cost')) {
      return 'w-32'; // 128px
    }
    if (columnLower.includes('impression') || columnLower.includes('click') || columnLower.includes('purchase')) {
      return 'w-28'; // 112px
    }
    if (columnLower.includes('ctr') || columnLower.includes('cvr') || columnLower.includes('roas') || columnLower.includes('cpa') || columnLower.includes('cpc')) {
      return 'w-24'; // 96px
    }
    if (columnLower.includes('date')) {
      return 'w-32'; // 128px
    }
    if (columnLower.includes('type') || columnLower.includes('format')) {
      return 'w-32'; // 128px
    }
    
    // Default width for other columns
    return 'w-40'; // 160px
  };

  const exportToCSV = () => {
    const csvContent = [
      columns.join(','),
      ...data.map(row => 
        columns.map(col => {
          const value = row[col];
          return typeof value === 'string' && value.includes(',') 
            ? `"${value}"` 
            : value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'showstop-data.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        No data available
      </div>
    );
  }

  return (
    <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-700/50">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-white">
            {data.length} records
          </span>
          {totalPages > 1 && (
            <span className="text-sm text-slate-400">
              Page {currentPage} of {totalPages}
            </span>
          )}
        </div>
        <button
          onClick={exportToCSV}
          className="flex items-center space-x-2 px-3 py-1 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 rounded-md transition-colors"
        >
          <Download className="h-4 w-4" />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-700/50">
          <thead className="bg-slate-700/30">
            <tr>
              {columns.map((column) => (
                <th
                  key={column}
                  onClick={() => requestSort(column)}
                  className={`${getColumnWidth(column)} px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-700/50 transition-colors`}
                >
                  <div className="flex items-center space-x-1">
                    <span className="truncate">{column.replace(/_/g, ' ')}</span>
                    {getSortIcon(column)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-slate-800/30 divide-y divide-slate-700/50">
            {paginatedData.map((row, index) => (
              <tr key={index} className="hover:bg-slate-700/30">
                {columns.map((column) => (
                  <td
                    key={column}
                    className={`${getColumnWidth(column)} px-6 py-4 text-sm text-slate-200`}
                  >
                    <div className="truncate" title={formatValue(row[column])}>
                      {formatValue(row[column])}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700/50">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm text-slate-300 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-slate-400">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm text-slate-300 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable; 